import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Check, X as XIcon, RefreshCw } from 'lucide-react';
import { API, getAuthHeaders } from '../../utils/adminAuth';

/**
 * Admin: list and fulfil monthly Top-Seller prize claims.
 * Each claim is filed by an ambassador from the certificate page; admin
 * marks it `fulfilled` once the cash + bottle + spotlight have been sent.
 */
const STATUS_COLORS = {
  pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
  fulfilled: 'text-[#2E9B50] bg-[#1C7D3F]/10 border-[#1C7D3F]/30',
  cancelled: 'text-red-400 bg-red-400/10 border-red-400/30',
};

export default function PrizeClaimsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const r = await axios.get(`${API}/admin/prize-claims${q}`, { headers: getAuthHeaders() });
      setItems(r.data || []);
    } catch {
      toast.error('Failed to load claims');
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = useCallback(async (claim, status) => {
    let adminNote = '';
    if (status === 'cancelled') {
      adminNote = window.prompt('Why cancel this claim? (visible internally)', '') || '';
    } else {
      adminNote = window.prompt('Optional internal note (e.g. GCash ref no., tracking #):', '') || '';
    }
    try {
      await axios.put(`${API}/admin/prize-claims/${claim.id}`,
        { status, admin_note: adminNote },
        { headers: getAuthHeaders() });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  }, [load]);

  const counts = items.reduce((acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }), {});
  const filters = [
    { key: 'all', label: 'All', count: items.length },
    { key: 'pending', label: 'Pending', count: counts.pending || 0 },
    { key: 'fulfilled', label: 'Fulfilled', count: counts.fulfilled || 0 },
    { key: 'cancelled', label: 'Cancelled', count: counts.cancelled || 0 },
  ];

  return (
    <div data-testid="prize-claims-tab" className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-2">
          <Gift className="w-3.5 h-3.5" /> Monthly Top-Seller Prizes
        </p>
        <h2 className="text-xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Prize Claims</h2>
        <p className="text-white/40 text-xs font-sans mt-1">
          When an ambassador claims their top-seller prize from the certificate page, it lands here.
          Mark fulfilled once cash bonus (GCash) + free bottle + spotlight post are all done.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`prize-filter-${f.key}`}
            className={`px-3 py-1.5 text-xs font-sans border transition-colors ${
              filter === f.key
                ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-medium'
                : 'border-white/15 text-white/60 hover:border-white/30 hover:text-white'
            }`}
          >
            {f.label} <span className="opacity-60">({f.count})</span>
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto px-3 py-1.5 text-xs font-sans border border-white/15 text-white/60 hover:text-white flex items-center gap-1.5"
          data-testid="prize-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm font-sans text-center py-10">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-white/40 text-sm font-sans text-center py-10 border border-dashed border-white/10">
          No prize claims yet for this filter.
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/5">
          <table className="w-full text-sm font-sans">
            <thead className="bg-[#141414] text-white/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Month</th>
                <th className="text-left p-3">Ambassador</th>
                <th className="text-right p-3">Bottles / Revenue</th>
                <th className="text-right p-3">Cash Prize</th>
                <th className="text-left p-3">Their Note</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`prize-row-${c.id}`}>
                  <td className="p-3 text-white">{c.month_label}</td>
                  <td className="p-3 text-white">
                    <div className="leading-tight">
                      <div>{c.ambassador_name || '—'}</div>
                      <div className="text-white/40 text-[11px]">{c.ambassador_email}</div>
                    </div>
                  </td>
                  <td className="p-3 text-right text-white/70">
                    {c.bottles} · ₱{Number(c.revenue || 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-right text-[#D4AF37] font-medium">
                    ₱{Number(c.cash_bonus || 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-white/60 max-w-[260px]">
                    <div className="line-clamp-3 text-xs">{c.note || <span className="text-white/30 italic">no note</span>}</div>
                    {c.admin_note && (
                      <div className="text-[10px] text-white/30 italic mt-1">Internal: {c.admin_note}</div>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border ${STATUS_COLORS[c.status] || ''}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {c.status !== 'fulfilled' && (
                        <button
                          onClick={() => updateStatus(c, 'fulfilled')}
                          title="Mark fulfilled"
                          data-testid={`prize-fulfil-${c.id}`}
                          className="p-1.5 bg-[#1C7D3F] text-white hover:bg-[#2E9B50]"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {c.status !== 'cancelled' && (
                        <button
                          onClick={() => updateStatus(c, 'cancelled')}
                          title="Cancel claim"
                          data-testid={`prize-cancel-${c.id}`}
                          className="p-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10"
                        >
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
